import ArticleCard from "@components/Cards/ArticleCard";
import { CompanionAdsLb } from "@elements/Advertisement/variant/CompanionAdsLb";
import fetchTablaApi from "@helper/fetchTablaApi";
import { displayAfter, isArrayWithElements } from "@helper/utils";
import { useTablaCustomEventAnalyticsCall } from "@hooks/useAnalyticsCall/useTablaCustomEventAnalyticsCall";
import {
  CreateLoadMoreHandler,
  CreateLoadMoreHandlerProps,
} from "@hooks/useLoadMore/CreateLoadMoreHandler";
import LoadMore from "@hooks/useLoadMore/LoadMore";
import useSetFirstRenderedArticleIds from "@hooks/useLoadMore/SetFirstRenderedArticleIds";
import { ProcessedArticleData } from "@transformer/useOsResponse";
import { AdvertisementTypeEnum, PageAdTargetValue } from "@typings/Ads.d";
import { MoreArticlesRequestData } from "@typings/MoreArticlesApi";
import React, { ReactElement, useState } from "react";

type SearchInfiniteScrollProps = {
  searchedArticles: ProcessedArticleData[];
  searchedText: string;
  pageAdTargetValue: PageAdTargetValue;
  resultCount: number;
};

const PAGE_SIZE = 10;
const INITIAL_ARTICLE_COUNT = 10;

export default function SearchInfiniteScroll({
  searchedArticles,
  searchedText,
  resultCount,
  pageAdTargetValue,
}: SearchInfiniteScrollProps): ReactElement {
  const [hasMoreStories, setHasMoreStories] = useState<boolean>(true);
  const [renderedArticleIds, setRenderedArticleIds] = useState(
    new Set<string>(),
  );

  const [pageNumber, setPageNumber] = useState<number>(1);
  const [loadMoreData, setLoadMoreData] = useState<ProcessedArticleData[]>([]);
  const showLoadMore: boolean =
    searchedArticles.length == INITIAL_ARTICLE_COUNT;
  useSetFirstRenderedArticleIds(searchedArticles, setRenderedArticleIds);
  const TablaCustomGaEvent = useTablaCustomEventAnalyticsCall();

  const handleLoadMore = async () => {
    const lastLoadedArticle =
      loadMoreData.length == 0 ? searchedArticles.at(-1) : loadMoreData.at(-1);
    const requestData: MoreArticlesRequestData = {
      filterArticlesBasedOn: searchedText,
      sort: lastLoadedArticle?.sort,
    };

    const fetchMoreTagsStoriesData = async (
      requestData: MoreArticlesRequestData,
    ): Promise<ProcessedArticleData[] | []> => {
      const queryParams = new URLSearchParams({
        searchedText: requestData.filterArticlesBasedOn as string,
        sort: Array.isArray(requestData.sort) ? requestData.sort.join(",") : "",
        size: "10",
      }).toString();

      const response: ProcessedArticleData[] = await fetchTablaApi(
        `more-search-stories?${queryParams}`,
        "GET",
      );
      return response;
    };
    const createLoadMoreProps: CreateLoadMoreHandlerProps = {
      requestData,
      fetchFunction: fetchMoreTagsStoriesData,
      setHasMoreStories,
      setRenderedArticleIds,
      setLoadMoreData,
      setPageNumber,
      loadMoreData,
      renderedArticleIds,
      lastLoadedArticle,
      pageSize: PAGE_SIZE,
      pageNumber,
    };

    const response = await CreateLoadMoreHandler(createLoadMoreProps);
    if (isArrayWithElements(response)) {
      TablaCustomGaEvent({
        event: "virtual_pv",
        pagination: pageNumber == 0 ? 1 : pageNumber,
      });
    }
    return response;
  };

  return (
    <>
      <div className="w-full relative my-5 xmd:mb-xxs px-3 xl:px-0">
        <span className="w-full block border-b border-gray-800 text-gray-500 text-sm font-normal leading-5">
          {resultCount} Results
        </span>
      </div>

      {[...searchedArticles, ...loadMoreData].map((context, index) => (
        <React.Fragment key={index}>
          <ArticleCard
            key={context.id}
            displayWidth={248}
            displayHeight={186}
            {...context}
            elements={{ hasParagraph: false, hasDate: true }}
            overrideStyle={{
              cardWrapper: `pt-0 pb-5 flex-row w-full xmd:w-[100%] lg:border-b-0 xmd:py-2.5 px-3 xl:px-0`,
              titleClass:
                "font-bold font-opensans text-base xmd:text-xl text-grey-500 leading-7 hover:no-underline",
              imageWrapperStyle: `w-[140px] xmd:w-[248px]`,
              imageStyle: "min-w-34 w-full",
              dateClass: "text-sm",
              contentWrapper: "gap-4",
            }}
          />
          {displayAfter(index + 1, PAGE_SIZE) && (
            <CompanionAdsLb
              adType={AdvertisementTypeEnum.LB3}
              uniqueSectionName="search"
              index={(index + 1) / PAGE_SIZE}
              pageAdTargetType={pageAdTargetValue}
              rootClassName={
                "mb-5 xmd:my-2.5 relative w-full bg-grey-950 py-xxm pt-2.5 pb-5 md:px-2 md:py-xxs lg:pt-4 lg:pb-6 lg:px-0"
              }
            />
          )}
        </React.Fragment>
      ))}

      {showLoadMore && (
        <div className="flex w-full justify-center mb-xl">
          <LoadMore
            rootClassName="my-4 w-full"
            onLoadMore={handleLoadMore}
            hasMore={hasMoreStories}
            loadText="Load More"
            mobileInfiniteScroll={true}
            isSearch={true}
            loadDefaultGaEvent={false}
          />
        </div>
      )}
    </>
  );
}
